
import { FC, useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { Button, Buttons, Tag, Message, Messages, Tooltip } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, Grid, useResponsive, useResponsiveDisplayMode } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";

import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";

import { NDCDropdown } from "./NDCDropdown";

import { formularyGetByMemberAPI, formularyListByIdAPI } from "@/api";
import { MedicalEligibilityResponse } from "@/api/interfaces/Medical";

export interface AccumulationsData {
  data: {
    family_accumulated: string;
    family_maximum: string;
    individual_accumulated: string;
    individual_maximum: string;
  };
}

export interface AccumulationsBarChartProps {
  accumulations: Record<string, AccumulationsData["data"]>;
}

interface DrugCoverageFormData {
  ndc: string;
  ndc_label?: string; // Store the full label with drug name
}


interface RawFormularyRecord {
  effective_date: string;
  end_date: string;
  start_date: string;
  termination_date: string;
  formulary_id: number;
  label_name: string;
  ndc: string;
  tier: string;
  network?: string;
  edits?: string[];
  quantity_limit?: number;
}

interface FormularyListResponseItem {
  formulary_record_new: RawFormularyRecord;
}

const EDIT_ATTRIBUTE_MAP: Record<string, string> = {
  quantity_limit: "QL",
  prior_authorization: "PA",
  step_therapy: "ST",
  gender_limit: "GL",
  age_limit_original: "AL1",
  custom: "C",
  specialty: "S",
  quantity_limit_custom: "QLC",
  compound_kit: "CK",
  affordable_care_act: "ACA",
  high_cost_brand: "HCB",
  high_cost_generic: "HCG",
};

function extractEdits(record: Record<string, any>): string[] {
  return Object.entries(EDIT_ATTRIBUTE_MAP)
    .filter(([key]) => {
      const val = record[key];
      return val && val !== "" && val !== "0" && val !== "false";
    })
    .map(([, code]) => code);
}

export const DrugCoverage: FC<{ member: MedicalEligibilityResponse }> = ({ member }) => {
  // Use parent form context (BenefitSearchForm provides Form) to avoid nested <form> elements
  const form = useFormContext<DrugCoverageFormData>();
  const ndcValue = useWatch({ control: form?.control, name: "ndc" });

  const [results, setResults] = useState<RawFormularyRecord[]>([]);
  const [rawFormularyRecords, setRawFormularyRecords] =
    useState<RawFormularyRecord[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchedNdc, setSearchedNdc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const displayMode = useResponsiveDisplayMode();
  const isMobile = displayMode === "mobile";

  const isSearchDisabled =
    isLoading ||
    !ndcValue;

  const { four, two } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);
  useEffect(() => {
    console.log("useWatch(ndc):", ndcValue);
  }, [ndcValue]);
  const handleSearch = useCallback(() => {
    try {
      const value = ndcValue?.trim();

      if (!value) {
        setErrorMessage(null);
        return;
      }


      if (isLoading) {
        setErrorMessage("Formulary data is still loading. Please try again.");
        return;
      }

      // Validation rules
      const isHCPCS = /^[A-V][0-9]{4}$/i.test(value);

      if (!isHCPCS && value.length < 4) {
        setErrorMessage(
          "Please enter at least 4 characters for a drug name, NDC, or GPI.",
        );
        setResults([]);
        return;
      }

      setErrorMessage(null);
      setSearchedNdc(value);

      console.log("SEARCH VALUE:", value);
      console.log("FIRST RECORD:", rawFormularyRecords[0]);
      const match = rawFormularyRecords.find(
        (r) =>
          r.ndc === value ||
          r.label_name.toLowerCase().includes(value.toLowerCase()),
      );



      if (match) {
        setResults([match]);
      } else {
        setResults([]);
      }


    } catch (err) {
      console.error("Drug coverage search failed:", err);
      setErrorMessage(
        "An unexpected error occurred while searching for drug coverage.",
      );
      setResults([]);
    }
  }, [ndcValue, isLoading, rawFormularyRecords]);

  const getEditBadge = (edit: string) => {
    switch (edit) {
      case "PA":
        return <Tag type="success-high-contrast">PA</Tag>;
      case "ST":
        return <Tag type="error-high-contrast">ST</Tag>;
      case "QL":
        return <Tag type="error-high-contrast">QL</Tag>;
      case "GL":
        return <Tag type="warning-high-contrast">GL</Tag>;
      case "AL1":
        return <Tag type="warning-high-contrast">AL1</Tag>;
      case "C":
        return <Tag type="info-high-contrast">C</Tag>;
      case "S":
        return <Tag type="info-high-contrast">S</Tag>;
      case "QLC":
        return <Tag type="error-high-contrast">QLC</Tag>;
      case "ACA":
        return <Tag type="info-high-contrast">ACA</Tag>;
      case "HCB":
        return <Tag type="warning-high-contrast">HCB</Tag>;
      case "HCG":
        return <Tag type="warning-high-contrast">HCG</Tag>;
      case "CK":
        return <Tag type="info-high-contrast">CK</Tag>;
      default:
        return null;
    }
  };

  useEffect(() => {
    const loadFormularyData = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);

        const formularyInfo = await formularyGetByMemberAPI({
          external_member_id: member.external_member_id,
          member_first_name: member.member_first_name,
          member_last_name: member.member_last_name,
          member_date_of_birth: member.member_date_of_birth,
        });

        console.log("FORMULARY INFO:", formularyInfo);

        if (!formularyInfo?.formulary_id) {
          throw new Error("formulary_id missing from formularyInfo");
        }

        const recordsResponse = await formularyListByIdAPI({
          formulary_id: formularyInfo.formulary_id,
        });

        console.log("RECORDS RESPONSE:", recordsResponse);

        if (!Array.isArray(recordsResponse?.results)) {
          throw new Error("Expected results array from formularyListByIdAPI");
        }

        const normalizedRecords = recordsResponse.results.map(
          (item: FormularyListResponseItem) => ({
            ...item.formulary_record_new,
            edits: extractEdits(item.formulary_record_new),
          }),
        );

        setRawFormularyRecords(normalizedRecords);
      } catch (err) {
        console.error("Failed to load formulary data:", err);
        setRawFormularyRecords([]);
        setErrorMessage("Unable to load formulary data for this member.");
      } finally {
        setIsLoading(false);
      }
    };

    loadFormularyData();
  }, [member]);


  return (!isLoading &&
    <Stack spacing="medium-large">
      <Stack spacing="small">
        <NDCDropdown name="ndc" label="Drug" testId="test__drug_coverage_ndc_dropdown" />
        <Body size="small" style={{ color: "#666" }}>
          Search with a minimum of 4 characters for drug name, NDC/GPI, or exact match for HCPCS code
        </Body>
        <Shelf>
          <Button
            appearance="primary"
            disabled={isSearchDisabled}
            onClick={(e: any) => {
              // prevent parent form submission/navigation
              e?.preventDefault?.();
              handleSearch();

            }}

          >
            {isLoading ? "Searching..." : "Search"}
          </Button>
          <Shelf.Spacer />
        </Shelf>
      </Stack>

      {errorMessage && (
        <Message appearance={Messages.Error} visible>
          {errorMessage}
        </Message>
      )}


      {searchedNdc &&
        results.length === 0 &&
        !errorMessage &&
        <Message appearance={Messages.Warning} visible>
          This drug is not part of the member's formulary.
        </Message>
      }


      {results.length > 0 && (
        <Stack spacing="small">
          {isMobile ? (
            // Mobile view
            results.map((result, index) => (
              <Stack key={index} spacing="small" style={{ paddingTop: "16px", paddingBottom: "16px", borderBottom: "1px solid #E0E0E0" }}>
                <Shelf spacing="small">
                  <Semibold style={{ fontSize: "16px" }}>{result.label_name}</Semibold>
                  <Shelf.Spacer />
                  <Shelf spacing="mini">
                    <Tooltip alignment="top" content="View formulary for more drug info">
                      {(ref) => (
                        <Button ref={ref} appearance={Buttons.Functional.Tertiary.Small} icon="info-circle">
                          <span style={{ display: "none" }}>Info</span>
                        </Button>
                      )}
                    </Tooltip>
                    <Tooltip alignment="top" content="Find best price">
                      {(ref) => (
                        <Button ref={ref} appearance={Buttons.Functional.Tertiary.Small} icon="dollar-circle">
                          <span style={{ display: "none" }}>Cost</span>
                        </Button>
                      )}
                    </Tooltip>
                  </Shelf>
                </Shelf>

                <Shelf spacing="small">
                  <Body size="small" style={{ fontWeight: 600, color: "#60646D" }}>Network</Body>
                  <Shelf.Spacer />
                  <Body> -- </Body>
                  <Body> {result.network}</Body>
                </Shelf>

                <Shelf spacing="small">
                  <Body size="small" style={{ fontWeight: 600, color: "#60646D" }}>Tier</Body>
                  <Shelf.Spacer />
                  <Body>Tier {result.tier}</Body>
                </Shelf>

                <Shelf spacing="small">
                  <Body size="small" style={{ fontWeight: 600, color: "#60646D" }}>Edits</Body>
                  <Shelf.Spacer />
                  <Shelf spacing="mini">
                    {result.edits && result.edits.length > 0 ? (
                      result.edits.map((edit, idx) => (
                        <span key={idx}>{getEditBadge(edit)}</span>
                      ))
                    ) : <Body>--</Body>}
                  </Shelf>
                </Shelf>

                <Shelf spacing="small">
                  <Body size="small" style={{ fontWeight: 600, color: "#60646D" }}>Days Supply</Body>
                  <Shelf.Spacer />
                  <Body>{result.quantity_limit || "--"}</Body>
                </Shelf>
              </Stack>
            ))
          ) : (
            // Desktop view
            <>
              {/* Header row */}
              <Grid style={{ borderBottom: "1px solid #E0E0E0", paddingBottom: "8px" }}>
                <Grid.Span columns={four}>
                  <Body size="small" style={{ fontWeight: 600, color: "#60646D" }}>DRUG & NETWORK</Body>
                </Grid.Span>
                <Grid.Span columns={two}>
                  <Body size="small" style={{ fontWeight: 600, color: "#60646D" }}>FORMULARY TIER</Body>
                </Grid.Span>
                <Grid.Span columns={two}>
                  <Body size="small" style={{ fontWeight: 600, color: "#60646D" }}>EDITS</Body>
                </Grid.Span>
                <Grid.Span columns={two}>
                  <Body size="small" style={{ fontWeight: 600, color: "#60646D" }}>DAYS SUPPLY</Body>
                </Grid.Span>
                <Grid.Span columns={two}>
                  <div></div>
                </Grid.Span>
              </Grid>

              {
                results.map((result, index) => (
                  <Grid key={index} style={{ paddingTop: "12px", paddingBottom: "12px" }}>
                    <Grid.Span columns={four}>
                      <Stack spacing="none">
                        <Semibold style={{ fontSize: "16px" }}>{result.label_name}</Semibold>
                        <Body size="small" style={{ color: "#60646D" }}>{result.network}</Body>
                      </Stack>
                    </Grid.Span>
                    <Grid.Span columns={two}>
                      <Body>Tier {result.tier}</Body>
                    </Grid.Span>
                    <Grid.Span columns={two}>
                      <Shelf spacing="mini" style={{ justifyContent: "flex-start" }}>
                        {result.edits && result.edits.length > 0 ? (
                          result.edits.map((edit, idx) => (
                            <span key={idx}>{getEditBadge(edit)}</span>
                          ))
                        ) : <span>--</span>}
                      </Shelf>
                    </Grid.Span>
                    <Grid.Span columns={two}>
                      <Body>{result.quantity_limit || "--"}</Body>
                    </Grid.Span>
                    <Grid.Span columns={two}>
                      <Shelf spacing="mini">
                        <Tooltip alignment="top" content="View formulary for more drug info">
                          {(ref) => (
                            <Button ref={ref} appearance={Buttons.Functional.Tertiary.Small} icon="info-circle">
                              <span style={{ display: "none" }}>Info</span>
                            </Button>
                          )}
                        </Tooltip>
                        <Tooltip alignment="top" content="Find best price">
                          {(ref) => (
                            <Button ref={ref} appearance={Buttons.Functional.Tertiary.Small} icon="dollar-circle">
                              <span style={{ display: "none" }}>Cost</span>
                            </Button>
                          )}
                        </Tooltip>
                      </Shelf>
                    </Grid.Span>
                  </Grid>
                ))
              }
            </>
          )}
        </Stack >
      )
      }
    </Stack >
  );
};
